import { FC } from 'react';
import { QueryClient } from '@tanstack/react-query';

import { type Theme } from '@dbt-labs/sourdough';

import { DbtCloudProject } from './context/DbtCloudProvider';
import { GlobalProviders } from './context/GlobalProviders';
import { kebabCaseFeatureFlagSet } from './featureFlags';
import { useAccount } from './hooks/admin/account';
import { useLinkToExplorer } from './hooks/links';
import { ExplorerRoutes } from './routes';
import { AccountParams, LinkGenerator, UrlProjectParams } from './util/linkTypes';

import './index.css';

export { DevelopmentContextProvider } from './context/DevelopmentContextProvider';
export * as featureFlags from './featureFlags';

const useLinkToProjects = (): LinkGenerator<AccountParams> =>
  useLinkToExplorer('multiProject');

const useLinkToProject = (): LinkGenerator<AccountParams & UrlProjectParams> =>
  useLinkToExplorer('project');

export {
  kebabCaseFeatureFlagSet,
  useAccount,
  useLinkToExplorer,
  useLinkToProject,
  useLinkToProjects,
};

type ExplorerContext = {
  discoveryUri?: string;
  dbtCloudAccountName: string | undefined;
  dbtCloudProjects: DbtCloudProject[] | undefined;
  currentAccountId: number | undefined;
  currentProjectId: number | undefined;
  /** Does the current user have a developer license (part of the /api/v2/whoami response) */
  hasDeveloperLicense: boolean;
  /** Does the current user have admin permissions */
  isAdmin: boolean;
  defaultEnvironmentId: number | 'NO_DEFAULT_ENVIRONMENT' | undefined;
  reactQueryClient: QueryClient;
  appContext: 'dbt-cloud' | 'snowflake-app' | 'cloud-ui';
  isSinglePaneView?: boolean;
  urlPrefix?: string;
  theme?: Theme;
};

export const Explorer: FC<ExplorerContext> = (props) => {
  const dbtCloudProjectName = props.dbtCloudProjects?.find(
    (p) => p.id === props.currentProjectId,
  )?.name;

  return (
    <GlobalProviders
      {...props}
      isSinglePaneView={props.isSinglePaneView ?? false}
      dbtCloudProjectName={dbtCloudProjectName ?? 'Overview'}
      key={props.currentAccountId}
    >
      <ExplorerRoutes isVerticalSlice={props.appContext === 'dbt-cloud'} />
    </GlobalProviders>
  );
};
